import { derived, get, writable } from "svelte/store";
import { regularizedPhone } from "../../../../assets/js/src/general/utils";
import { SwDerivedApiStore } from "../../../api-stores/sw-api-store";
import { tagsStore } from "../../../api-stores/tags-store";
import {
  userHasContactPanelAIStore,
  userStore,
} from "../../../api-stores/user-store";
import {
  fillFeedActivities,
  getContactRecords,
  getProfileRecords,
  getTrackingActivities,
} from "../../../api/activities";
import { getCampaigns } from "../../../api/campaigns";
import { getCandidate, getSearches } from "../../../api/candidates";
import { swWritable } from "../../../utils/sw-writable";
import { flaggedContacts } from "./ProfileContactSideBar/stores";
import { extractGetSearchesEmails } from "./utils";

// Populate with an item in the array of responses from the todos query endpoint.
export const todosModalItemStore = writable(null);

export const showEditContact = writable(false);

const candidateIdArgStore = derived(
  [todosModalItemStore],
  ([$todosModalItemStore]) => {
    if (!$todosModalItemStore) return null;
    const { candidateId } = $todosModalItemStore;
    if (!candidateId) return null;
    return [candidateId];
  },
);

// BE returns null if candidate not found, but FE expects an object.
// Use a buffer store to catch this, then process in the real candidateStore below.
export const candidateApiStore = SwDerivedApiStore({
  get: getCandidate,
  argsStore: candidateIdArgStore,
  onMessage: ({ message, update, get: candidateGet }) => {
    if (message.kind !== "update-candidates") return;

    // Lock candidateStore when editing contact
    if (get(showEditContact)) return;

    // Multiple changes sent together can be related to this candidate
    message.changes.forEach((change) =>
      update((current) => {
        if (!current) return null; // Don't try to update if the candidate is null

        if (change.ids.includes(current.candidateId)) {
          // BE does some extra manipulation to phones and social links
          // so we need to do another api call if these are changed.
          if (change.update?.phones || change.update?.socialLinks) {
            candidateGet();
          }
          // return the changed candidate whether we've entered the above if block or not
          return { ...current, ...change.update };
        }

        return current;
      }),
    );
  },
});

export const candidateStore = derived(
  [candidateApiStore, todosModalItemStore],
  ([$candidateApiStore, $todosModalItemStore]) => {
    if (!$candidateApiStore) {
      if (!$todosModalItemStore) return {};

      // Return a reduced todosModalItemStore if the candidate is not found
      const { next, prev, callback, ...filteredItems } = $todosModalItemStore;
      return { ...filteredItems, isDeleted: true };
    }
    return $candidateApiStore;
  },
);

export const avatarProps = derived(
  [todosModalItemStore, candidateStore],
  ([$todosModalItemStore, $candidateStore]) => {
    const cId1 = $todosModalItemStore?.candidateId;
    const cId2 = $candidateStore?.candidateId;
    const skeleton = !cId1 || !cId2 || cId1 !== cId2;

    return {
      src: $candidateStore.photo || "",
      firstName: $candidateStore?.firstName || "",
      lastName: $candidateStore?.lastName || "",
      skeleton,
    };
  },
);

const openCampaignsArgsStore = derived([userStore], ([$userStore]) => {
  const currentUserOnly = !(
    $userStore.kind === "superUser" ||
    ($userStore.isKind === "viewer" && $userStore.isAdmin)
  );
  return [{ openOnly: true, currentUserOnly }];
});

export const openCampaignsStore = SwDerivedApiStore({
  get: getCampaigns,
  argsStore: openCampaignsArgsStore,
  init: () => [],
});

export const campaignOptions = derived(
  openCampaignsStore,
  ($openCampaignsStore) =>
    ($openCampaignsStore || []).map((campaign) => ({
      id: campaign.campaignId,
      label: campaign.campaignName,
      icon: "fa-regular fa-megaphone",
      creatorUserId: campaign.creatorUserId,
    })),
);

export const selectedCampaignIdStore = writable("");

export const showAddToCampaignModal = writable(false);

const contactRecordsStoreArg = derived(
  [candidateStore, todosModalItemStore],
  ([$candidateStore, $todosModalItemStore]) => {
    const { candidateId, emails = [] } = $candidateStore;
    const cId = $todosModalItemStore?.candidateId;
    if (!cId || !candidateId || cId !== candidateId) return null;
    const items = emails.map((email) => ({ contact: email, candidateId }));
    return [{ items, useSavedCandidates: true }];
  },
);

export const contactRecordsStore = SwDerivedApiStore({
  get: getContactRecords,
  init: () => [],
  argsStore: contactRecordsStoreArg,
});

export const profileRecordsStore = SwDerivedApiStore({
  get: getProfileRecords,
  argsStore: todosModalItemStore,
  argsTransform: ($todosModalItemStore) => {
    if (!$todosModalItemStore) return null;
    const { candidateId } = $todosModalItemStore;
    if (!candidateId) return null;
    return [{ candidateIds: [candidateId], current: true }];
  },
});

export const candidatePhoneStore = derived(
  [candidateStore, profileRecordsStore, flaggedContacts],
  ([$candidateStore, $profileRecordsStore, $flaggedContacts]) => {
    const candidate = $candidateStore;
    if (!candidate) return [];
    const records = $profileRecordsStore;
    const record = records[candidate.candidateId] || {};

    // Combine candidate and profile records to get all phone numbers
    // - from least to best source.
    const phonesByNumber = {};

    [candidate, record].forEach((src) => {
      ["landline", "mobile", "other"].forEach((kind) => {
        const key = `${kind}Phones`;
        if (!src.phoneObjects || !src.phoneObjects[key]) return;
        src.phoneObjects[key].forEach((phone) => {
          const contact = regularizedPhone(phone.phone);
          if (!$flaggedContacts.includes(contact.trim())) {
            phonesByNumber[contact] = { ...phone, kind };
          }
        });
      });
    });
    return Object.values(phonesByNumber);
  },
);

export const callAnsweredStore = writable({});

const TodoPanelStore = () => {
  const store = swWritable({});

  let toDoId = null;

  todosModalItemStore.subscribe(($todosModalItemStore) => {
    if (!$todosModalItemStore) {
      store.set({}, true);
      return;
    }

    // Don't reset the store if the toDoId hasn't changed
    // Prevents e.g. phone call notes disappearing when websocket messages are received
    if (toDoId === $todosModalItemStore.toDoId) return;

    const { kind } = $todosModalItemStore;

    const init = () => {
      // initialise store with default values to ensure bindings don't change
      // dirty sub-store (see YourToDo.svelte)
      if (kind === "sendEmailAfterInmail") {
        return $todosModalItemStore.data.messageObj;
      }
      if (kind === "phoneCall") {
        return { phoneCallNote: "" };
      }
      return {};
    };

    store.set(init(), true);
    toDoId = $todosModalItemStore.toDoId;
  });

  return store;
};

export const todoPanelStore = TodoPanelStore();

export const selectedTableStore = writable({});

export const todoPanelTabs = derived(
  [todosModalItemStore, userHasContactPanelAIStore],
  ([$todosModalItemStore, $userHasContactPanelAIStore]) => {
    const tabItems = [
      { label: "Your To-do", slot: "tab0", onclick: () => {} },
      { label: "Contact Activity", slot: "tab1", onclick: () => {} },
      { label: "Email Engagement", slot: "tab2", onclick: () => {} },
      {
        label: "Company Insights",
        badge: "NEW",
        slot: "tab3",
        onclick: () => {},
      },
      {
        label: "Ask AI",
        badge: "BETA",
        slot: "tab4",
        onclick: () => {},
      },
    ];

    return tabItems.filter((tab) => {
      const OMIT_YOUR_TODO = [
        "opportunity",
        "sendInmail",
        "linkedinConnectionRequest",
      ];
      const OMIT_COMPANY_INSIGHTS = ["sendInmail", "linkedinConnectionRequest"];

      // Hide "Your To-Do" for contact modal, opportunities and LinkedIn todos
      if (tab.slot === "tab0")
        return (
          $todosModalItemStore?.toDoId &&
          !OMIT_YOUR_TODO.includes($todosModalItemStore?.kind)
        );

      // Hide "Company Insights" for contact modal and LinkedIn todos
      if (tab.slot === "tab3")
        return (
          $todosModalItemStore?.toDoId &&
          !OMIT_COMPANY_INSIGHTS.includes($todosModalItemStore?.kind)
        );

      // Hide "Ask AI" if the user doesn't have the feature flag
      if (tab.slot === "tab4") return $userHasContactPanelAIStore;

      // Show tabs 1 and 2 always
      return true;
    });
  },
);

export const saveStatusPayloadStore = derived(
  [todosModalItemStore, todoPanelStore],
  ([$todosModalItemStore, $todoPanelStore]) => {
    const toDoIds = $todosModalItemStore ? [$todosModalItemStore.toDoId] : [];
    const isOpportunity = $todosModalItemStore?.kind === "opportunity";
    const isAiToDo = $todosModalItemStore?.kind === "aiToDo";
    return { ...$todoPanelStore, toDoIds, isOpportunity, isAiToDo };
  },
);

export const activityFeedStore = SwDerivedApiStore({
  get: fillFeedActivities,
  init: () => ({ feed: [] }),
  argsStore: candidateIdArgStore,
});

// Augment the candidatePhoneStore with last messaged timestamps for each phone number
export const messagePhoneStore = derived(
  [activityFeedStore, candidatePhoneStore],
  ([$activityFeedStore, $candidatePhoneStore]) => {
    const lastMessaged = {};

    // 262 - "SMS Sent", 264 - "WhatsApp Sent"
    const activityCodeMap = { 262: "sms", 264: "whatsapp" };

    // Filter relevant message activities
    const messageActivities = $activityFeedStore.feed.filter(
      (activity) =>
        activity.activityCode &&
        Object.keys(activityCodeMap).includes(activity.activityCode.toString()),
    );

    messageActivities.forEach((activity) => {
      const type = activityCodeMap[activity.activityCode];
      const phoneNumber = regularizedPhone(activity.to);

      // Initialize entry for this phone number if it doesn't exist
      if (!lastMessaged[phoneNumber]) {
        lastMessaged[phoneNumber] = { whatsapp: null, sms: null };
      }

      // Update the timestamp if newer
      const currentValue = lastMessaged[phoneNumber][type];
      if (currentValue === null || activity.timestamp > currentValue) {
        lastMessaged[phoneNumber][type] = activity.timestamp;
      }
    });

    // Map candidatePhoneStore to include last messaged timestamps
    return $candidatePhoneStore.map((phoneEntry) => ({
      ...phoneEntry,
      lastMessaged: lastMessaged[regularizedPhone(phoneEntry.phone)] || {},
    }));
  },
);

const createTrackingStore = (activityType) =>
  SwDerivedApiStore({
    get: getTrackingActivities,
    init: () => [],
    argsStore: todosModalItemStore,
    argsTransform: ($todosModalItemStore) => {
      const { candidateId, toDoId, campaignId } = $todosModalItemStore || {};
      if (!candidateId) return null;
      const ret = { candidateId, kind: activityType };
      if (toDoId) ret.campaignId = campaignId; // only search for specific campaign on a todo
      return [ret];
    },
  });

export const clicksTrackingStore = createTrackingStore("click");
export const opensTrackingStore = createTrackingStore("open");

const createUniqueGroupedStore = (originalStore) => {
  const store = derived(originalStore, ($original) => {
    const groupedData = $original.reduce((acc, currentElement) => {
      const existingIndex = acc.findIndex(
        (item) =>
          item.campaignId === currentElement.campaignId &&
          item.stepNumber === currentElement.stepNumber,
      );

      if (existingIndex === -1) {
        acc.push({ ...currentElement, history: [], total: 1 });
      } else {
        acc[existingIndex].history.push(currentElement);
        acc[existingIndex].total = acc[existingIndex].history.length + 1;
      }

      return acc;
    }, []);

    return groupedData;
  });
  store.meta = originalStore.meta;
  return store;
};

export const opensGroupedStore = createUniqueGroupedStore(opensTrackingStore);
export const clicksGroupedStore = createUniqueGroupedStore(clicksTrackingStore);

// External email providers
//  - exclude Sourcewhale search for email and phone
//  - exclude kinds which only provide phone/mobile numbers
export const emailSearchKinds = derived(userStore, ($userStore) =>
  Object.entries($userStore?.searchKinds || {}).filter(
    ([kind]) =>
      !["email", "phone"].includes(kind) &&
      !kind.endsWith("--phone") &&
      !kind.endsWith("--mobile"),
  ),
);

// lastEnrichTime is used to trigger a re-fetch after manual enrichment.
export const lastEnrichTime = writable(null);
const suggestionsArgs = derived(
  [todosModalItemStore, lastEnrichTime],
  ([$todosModalItemStore, $lastEnrichTime]) => {
    if (
      !$todosModalItemStore?.candidateId ||
      $todosModalItemStore.kind !== "bounce"
    )
      return null;

    const searchParams = {
      resultIdsMap: { [$todosModalItemStore.candidateId]: ["all"] },
      checkBounces: true,
    };
    if (lastEnrichTime) {
      searchParams.timestamp = $lastEnrichTime;
    }

    return [searchParams];
  },
);

export const suggestionsStore = SwDerivedApiStore({
  get: getSearches,
  argsStore: suggestionsArgs,
  dataTransform: (data) => extractGetSearchesEmails(data),
});

// Editable fields shown in the edit contact section of the modal
export const editContactSections = [
  {
    header: "Personal Contact",
    items: [
      { label: "Name", id: "firstName", type: "text" },
      { label: "Last Name", id: "lastName", type: "text" },
      { label: "Job Title", id: "role", type: "text" },
      { label: "Company", id: "company", type: "text" },
      { label: "Email", id: "emails", type: "chip" },
      { label: "Phone", id: "phones", type: "chip" },
      { label: "Social Links", id: "socialLinks", type: "chip" },
    ],
  },
  {
    header: "Address",
    items: [
      { label: "City", id: "city", type: "text" },
      { label: "County / State", id: "state", type: "text" },
      { label: "Country", id: "country", type: "text" },
    ],
  },
  {
    header: "Other",
    items: [
      { label: "Tags", id: "tagIds", type: "dropdown" },
      { label: "Comments", id: "comments", type: "freeText" },
    ],
  },
];

// Removes invalid tagIds from the candidate
const filteredCandidateStore = derived(
  [candidateStore, tagsStore],
  ([$candidateStore, $tagsStore]) => {
    const tagIds = $tagsStore.map((tag) => tag.tagId);
    if ($candidateStore.tagIds) {
      $candidateStore.tagIds = $candidateStore.tagIds.filter((tagId) =>
        tagIds.includes(tagId),
      );
    }
    return $candidateStore;
  },
);

export const candidateTagStore = derived(
  [filteredCandidateStore, tagsStore],
  ([$filteredCandidateStore, $tagsStore]) =>
    ($filteredCandidateStore.tagIds || []).map((x) => ({
      id: x,
      title: $tagsStore.find((tag) => tag.tagId === x).title,
    })),
);

const EditContactStore = () => {
  const store = swWritable({});
  let defaultsStr = "{}";

  showEditContact.subscribe(($showEditContact) => {
    if (!$showEditContact) {
      store.set(JSON.parse(defaultsStr), true);
    }
  });
  // For comparing with the candidate store (allows dirty checking)
  // Subscribed to showEditContact to reset the store when the modal is closed
  filteredCandidateStore.subscribe((candidate) => {
    const candidateProperties = {};
    editContactSections.forEach((section) => {
      section.items.forEach((item) => {
        candidateProperties[item.id] =
          candidate[item.id] || (item.type === "chip" ? [] : "");
      });
    });
    defaultsStr = JSON.stringify(candidateProperties);
    store.set(candidateProperties, true);
  });
  return store;
};

export const editContactStore = EditContactStore();

// Keep track of modal height and decide whether to show smaller resolution config
// Used in ProfileCardHeader.svelte and ProfileInformation.svelte
export const modalBodyHeight = writable(0);
export const compactModal = derived(
  [modalBodyHeight],
  ([$modalBodyHeight]) => $modalBodyHeight < 538,
);

export const jobInfo = derived(
  [todosModalItemStore, candidateStore, candidateApiStore.meta],
  ([$todosModalItemStore, $candidateStore, $meta]) => {
    if (!$todosModalItemStore?.candidateId) return {};
    const skeleton = $meta?.fetching;
    if ($candidateStore?.candidateId === $todosModalItemStore?.candidateId) {
      return {
        skeleton,
        candidateRole:
          $candidateStore?.role || $todosModalItemStore?.candidateRole || "",
        candidateCompany:
          $candidateStore?.company ||
          $todosModalItemStore?.candidateCompany ||
          "",
        candidateName:
          $candidateStore?.name || $todosModalItemStore?.candidateName || "",
        candidateLocation:
          $candidateStore?.location ||
          $todosModalItemStore?.candidateLocation ||
          "",
        timezone: $candidateStore.timezone,
      };
    }
    const {
      candidateRole = "",
      candidateCompany = "",
      candidateLocation = "",
      candidateName = "",
    } = $todosModalItemStore || {};
    return {
      skeleton,
      candidateRole,
      candidateCompany,
      candidateLocation,
      candidateName,
    };
  },
);

// Keeps track of the active tab, useful for company insights
const ActiveTabStore = () => {
  const store = writable(null);

  // Make sure to reset the activeTabStore when the modal is closed
  todosModalItemStore.subscribe(($todo) => {
    if (!$todo) store.set(null);
  });

  return store;
};

export const activeTabStore = ActiveTabStore();

export const candidateSourceStore = derived(
  candidateStore,
  ($candidateStore) => ({
    source: $candidateStore.source,
    sourceName: $candidateStore.sourceName,
  }),
);

export const activePopUpModalStore = writable(null);
